import React from "react";

function PrivacyPolicyPage({ onOpenCookieSettings }) {
  return (
    <div className="container cookies-page-container">
      <div className="cookies-page-header">
        <img src="/logo.png" alt="Student Loan Modeller Logo" className="page-logo" />
        <h1 className="main-title cookies-page-title">Privacy Policy</h1>
        <p className="app-intro cookies-page-intro">
          This page explains how SLAM processes personal data and your privacy choices.
        </p>
      </div>

      <section className="cookies-section">
        <h2>Who This Policy Applies To</h2>
        <p>
          This Privacy Policy applies to users of the Student Loan Amount Modeller (SLAM) website.
          It explains what information we process when you use the site and why.
        </p>
        <p className="policy-effective-date"><strong>Last updated:</strong> 18 August 2026</p>
      </section>

      <section className="cookies-section">
        <h2>What Data We Process</h2>
        <p>
          Calculator inputs are processed only to provide the modelling results you request during your
          active session. In normal operation, SLAM is designed so that calculator inputs are not
          intentionally stored in application databases, analytics tools, or browser local storage.
        </p>
        <p>
          Depending on how you use SLAM, processed data may include values you type into the calculator
          (for example income and loan details), as well as technical usage data where analytics is enabled.
        </p>
        <ul className="cookies-detail-list">
          <li>Calculator input data used transiently to run projections.</li>
          <li>Preference data, including your cookie consent choice in local storage.</li>
          <li>
            Disclaimer acceptance record data in local storage: acceptance timestamp, disclaimer version, and a
            randomly generated browser identifier.
          </li>
          <li>
            Optional analytics data (only after consent), such as pages viewed, device/browser data,
            and a pseudonymous analytics identifier.
          </li>
        </ul>
      </section>

      <section className="cookies-section">
        <h2>How We Use Data</h2>
        <p>We use processed data to:</p>
        <ul className="cookies-detail-list">
          <li>Run the student loan modelling calculations and present results.</li>
          <li>Remember your cookie preference.</li>
          <li>Record which disclaimer version was actively accepted before calculator use.</li>
          <li>
            Understand aggregate usage patterns and improve the website when analytics has been accepted.
          </li>
        </ul>
      </section>

      <section className="cookies-section">
        <h2>Disclaimer Acceptance Records</h2>
        <p>
          Before using the calculator, users must actively accept the disclaimer. SLAM stores a minimal local
          record of that acceptance containing only:
        </p>
        <ul className="cookies-detail-list">
          <li>The date and time of acceptance.</li>
          <li>The disclaimer version that was accepted.</li>
          <li>A random browser identifier.</li>
        </ul>
        <p>
          This record is used to demonstrate which terms were presented and accepted. It is not used to identify
          you directly and does not require your name, email address, or equivalent identifiers. SLAM does not
          retain full IP addresses solely for this disclaimer acceptance purpose.
        </p>
      </section>

      <section className="cookies-section">
        <h2>Lawful Basis</h2>
        <p>
          Where UK GDPR applies, we rely on different lawful bases depending on the processing activity:
        </p>
        <ul className="cookies-detail-list">
          <li>
            <strong>Consent</strong> for optional analytics cookies and associated analytics processing.
          </li>
          <li>
            <strong>Legitimate interests</strong> for core service operation, security, maintaining the
            website, and evidencing disclaimer acceptance.
          </li>
        </ul>
      </section>

      <section className="cookies-section">
        <h2>Cookies and Analytics</h2>
        <p>
          SLAM uses Google Analytics 4 only if you choose <strong>Accept analytics</strong>.
          If you reject analytics, analytics tracking is not enabled.
        </p>
        <p>
          If you later withdraw consent, analytics collection stops and existing analytics cookies are
          removed where technically possible.
        </p>
        <p>
          For detailed cookie information, please see our <a href="/cookies">Cookies page</a>.
        </p>
      </section>

      <section className="cookies-section">
        <h2>Data Sharing</h2>
        <p>
          We do not sell personal data. Where analytics is enabled, usage information is processed by
          Google Analytics as a service provider.
        </p>
      </section>

      <section className="cookies-section">
        <h2>International Transfers</h2>
        <p>
          If analytics is enabled, data may be processed outside the UK by Google. Transfers are handled
          under Google&apos;s applicable contractual and technical safeguards.
        </p>
      </section>

      <section className="cookies-section">
        <h2>Data Retention</h2>
        <p>
          In normal operation, calculator inputs are not retained by the SLAM application after
          calculations are completed.
        </p>
        <p>
          Cookie preference data and disclaimer acceptance records remain in your browser until you clear
          site data or, where applicable, until a new disclaimer version requires acceptance.
          Analytics retention is controlled within Google Analytics settings.
        </p>
      </section>

      <section className="cookies-section">
        <h2>Your Rights</h2>
        <p>
          Depending on applicable law, you may have rights to access, correct, erase, or restrict the use of
          personal data, and to object to certain processing.
        </p>
        <p>
          You can manage analytics consent at any time through cookie settings.
        </p>
      </section>

      <section className="cookies-section">
        <h2>Security</h2>
        <p>
          We use reasonable technical and organisational measures intended to protect data processed through
          the website. No internet-based service can be fully guaranteed to be secure.
        </p>
      </section>

      <section className="cookies-section">
        <h2>Changes To This Policy</h2>
        <p>
          We may update this policy from time to time. Material changes will be reflected on this page with
          an updated revision date.
        </p>
      </section>

      <div className="cookies-actions">
        <button type="button" className="cookie-btn cookie-btn-secondary" onClick={onOpenCookieSettings}>
          Reopen cookie settings
        </button>
        <a className="cookie-btn cookie-btn-secondary" href="/terms">
          Terms of use
        </a>
        <a className="cookie-btn cookie-btn-secondary" href="/cookies">
          Cookies page
        </a>
        <a className="cookie-btn cookie-btn-primary" href="/">
          Back to calculator
        </a>
      </div>
    </div>
  );
}

export default PrivacyPolicyPage;